import type { HoldOption, HoldSpec, Moment, PieceData, WhatIf } from '../types';
import type { ChoraleJson } from './types';
import { FIRST_LINES } from './editorial';
import { RECOGNIZERS } from '../walk';
import { Key, degreeToken, keyName, parseKey, sameKey } from '../../music/key';
import { ChordToken, findFifthsSequences, matchPatterns } from '../../music/matcher';
import {
  HarmonyEvent,
  HarmonySpec,
  Measure,
  NoteEvent,
  PunctuationMark,
  PunctuationSpec,
  RegionSpec,
  ScriptSpec,
  barAt,
  barStart,
  buildHarmonyEvents,
} from '../../music/piece';
import { mod, pcName, pcNumber, pitchName } from '../../music/pitch';
import { Chord, realizeRoman } from '../../music/roman';
import { spellMidi, voiceChord } from '../../music/voicing';

/**
 * Everything the atlas shows about a chorale beyond its notes and harmony labels is
 * derived here by rule: key regions, cadences at the fermatas, phrases, recognized paths,
 * and a what-if and a hold for each authentic cadence. The same input always gives the
 * same output; nothing here is hand-written per chorale.
 */

export type CadenceKind = 'pac' | 'iac' | 'plagal' | 'deceptive' | 'half' | 'phrygian' | 'tonic' | 'pause';

export interface Cadence {
  at: number;
  bar: number;
  dur: number;
  kind: CadenceKind;
  key: Key;
  event: HarmonyEvent;
  prev?: HarmonyEvent;
}

export const CADENCE_MARK: Record<CadenceKind, PunctuationMark> = {
  pac: 'full',
  iac: 'semicolon',
  plagal: 'semicolon',
  tonic: 'semicolon',
  deceptive: 'ellipsis',
  half: 'comma',
  phrygian: 'comma',
  pause: 'comma',
};

export const CADENCE_NAMES: Record<CadenceKind, string> = {
  pac: 'Perfect authentic cadence',
  iac: 'Imperfect authentic cadence',
  plagal: 'Plagal cadence',
  tonic: 'Contrapuntal close on the tonic',
  deceptive: 'Deceptive cadence',
  half: 'Half cadence',
  phrygian: 'Phrygian half cadence',
  pause: 'Pause',
};

export const CADENCE_PATH: Partial<Record<CadenceKind, string>> = {
  pac: 'authentic-cadence',
  iac: 'authentic-cadence',
  deceptive: 'deceptive-cadence',
  half: 'half-cadence',
  phrygian: 'half-cadence',
};

const rootDegree = (c: Chord, k: Key) => mod(c.root.letter - k.tonic.letter, 7) + 1;
const isTonic = (c: Chord, k: Key) => c.fn === 'T' && !c.applied && !c.cadential && rootDegree(c, k) === 1 && pcNumber(c.root) === pcNumber(k.tonic);
const isDominant = (c: Chord, k: Key) => !c.applied && !c.cadential && (rootDegree(c, k) === 5 || (rootDegree(c, k) === 7 && c.triad === 'diminished')) && c.fn === 'D';

export function chordName(c: Chord): string {
  const q = c.triad === 'major' ? 'major' : c.triad === 'minor' ? 'minor' : c.triad === 'diminished' ? 'diminished' : 'augmented';
  return `${pcName(c.root)} ${q}`;
}

function eventAtTime(events: HarmonyEvent[], t: number): number {
  for (let i = events.length - 1; i >= 0; i--) if (events[i].start <= t + 1e-6) return i;
  return 0;
}

function soundingAt(notes: NoteEvent[], t: number): NoteEvent[] {
  return notes.filter((n) => n.onset <= t + 1e-6 && n.onset + n.dur > t + 1e-6);
}

export function classifyCadence(ev: HarmonyEvent, prev: HarmonyEvent | undefined, soprano: NoteEvent | undefined): CadenceKind {
  const k = ev.key;
  const c = ev.chord;
  const p = prev?.chord;
  const pk = prev?.key ?? k;
  if (p && isDominant(p, pk) && p.inversion === 0 && sameKey(pk, k) && isTonic(c, k) && c.inversion === 0 && rootDegree(p, pk) === 5) {
    return soprano && mod(soprano.midi, 12) === pcNumber(k.tonic) ? 'pac' : 'iac';
  }
  if (p && isDominant(p, pk) && rootDegree(p, pk) === 5 && c.fn === 'T' && rootDegree(c, k) === 6) return 'deceptive';
  if (p && p.fn === 'PD' && rootDegree(p, pk) === 4 && isTonic(c, k) && c.inversion === 0) return 'plagal';
  if (isDominant(c, k) && rootDegree(c, k) === 5 && c.inversion === 0) {
    const bassIn = p ? degreeToken(p.bass, pk) : '';
    return k.mode === 'minor' && p && rootDegree(p, pk) === 4 && bassIn === '6' ? 'phrygian' : 'half';
  }
  if (isTonic(c, k)) return 'tonic';
  return 'pause';
}

function sungText(notes: NoteEvent[], start: number, end: number): string {
  const syl = notes.filter((n) => n.line === 3 && n.lyric && n.onset >= start - 1e-6 && n.onset <= end + 1e-6).sort((a, b) => a.onset - b.onset);
  let out = '';
  for (const n of syl) {
    const s = n.lyric!;
    out += s.endsWith('-') ? s.slice(0, -1) : `${s} `;
  }
  return out.trim();
}

/** Voice a chord under a fixed melody note, continuing smoothly from the notes before it. */
function reharmonize(symbol: string, key: Key, before: NoteEvent[], prevChord: Chord | undefined, topMidi: number) {
  const chord = realizeRoman(symbol, key);
  if (!chord.pcs.includes(mod(topMidi, 12))) return null;
  const prev = before.length === 4 ? before.map((n) => n.midi).sort((a, b) => a - b) : null;
  try {
    const midi = voiceChord(chord, { voices: 4, prev, prevChord: prevChord ?? null, fixedTop: topMidi, bassRange: [36, 55], upperRange: [48, topMidi] });
    return { chord, midi, names: midi.map((m) => pitchName(spellMidi(m, chord.members))) };
  } catch {
    return null;
  }
}

const PATTERN_IDS = ['prinner', 'romanesca', 'fonte', 'monte', 'converging', 'home-orbit'];

/** Harmony labels that parse, in bars that exist, one per time point (the last wins). */
export function validHarmony(harmony: Array<{ bar: number; beat: number; key: string | null; rn: string; pivot?: { key: string; rn: string } }>, measures: Measure[]): HarmonySpec[] {
  const seen = new Map<string, HarmonySpec>();
  const bars = new Set(measures.map((m) => m.bar));
  for (const h of harmony) {
    if (!h.key || !bars.has(h.bar)) continue;
    try {
      realizeRoman(h.rn, parseKey(h.key));
    } catch {
      continue;
    }
    seen.set(`${h.bar}:${h.beat}`, { bar: h.bar, beat: h.beat, key: h.key, rn: h.rn, ...(h.pivot ? { pivot: h.pivot } : {}) });
  }
  return [...seen.values()];
}

/** Key regions by time, from the keys of the harmony labels. */
export function regionsOf(events: HarmonyEvent[], measures: Measure[], home: Key): RegionSpec[] {
  const regions: RegionSpec[] = [];
  events.forEach((ev) => {
    const last = regions[regions.length - 1];
    if (last && last.key === ev.spec.key) {
      last.end = ev.end;
      last.to = barAt(measures, ev.end - 1e-6);
      return;
    }
    const k = parseKey(ev.spec.key);
    const isHome = sameKey(k, home);
    const role = isHome ? (regions.length ? 'return' : 'home') : k.mode === 'minor' && home.mode === 'major' ? 'shadow' : 'away';
    regions.push({ from: ev.bar, to: barAt(measures, ev.end - 1e-6), key: ev.spec.key, role, start: ev.start, end: ev.end });
  });
  return regions;
}

/** Schemata, sequences and deceptive progressions found by rule in a chord succession. */
export function pathMatches(events: HarmonyEvent[]): Array<{ path: string; start: number; end: number; key: Key }> {
  const tokens: ChordToken[] = events.map((ev) => ({ symbol: ev.spec.rn, chord: ev.chord, key: ev.key }));
  const specs = RECOGNIZERS.filter((r) => PATTERN_IDS.includes(r.id));
  const raw = [...matchPatterns(tokens, specs), ...findFifthsSequences(tokens, 4)];
  const matches = raw.map((m) => ({ path: m.id, start: events[m.start].start, end: events[m.end].end, key: events[m.start].key }));
  events.forEach((ev, i) => {
    const p = events[i - 1];
    if (p && isDominant(p.chord, p.key) && rootDegree(p.chord, p.key) === 5 && p.chord.inversion === 0 && ev.chord.fn === 'T' && rootDegree(ev.chord, ev.key) === 6 && sameKey(p.key, ev.key)) {
      matches.push({ path: 'deceptive-cadence', start: p.start, end: ev.end, key: ev.key });
    }
  });
  return matches;
}

/** The non-overlapping script of paths shown above the music (longest first). */
export function scriptOf(matches: Array<{ path: string; start: number; end: number }>, measures: Measure[]): ScriptSpec[] {
  const script: ScriptSpec[] = [];
  [...matches]
    .filter((m) => m.path !== 'deceptive-cadence')
    .sort((a, b) => b.end - b.start - (a.end - a.start) || a.start - b.start)
    .forEach((m) => {
      if (script.some((s) => m.start < (s.end ?? 0) - 1e-6 && m.end > (s.start ?? 0) + 1e-6)) return;
      script.push({ from: barAt(measures, m.start), to: barAt(measures, m.end - 1e-6), path: m.path, start: m.start, end: m.end });
    });
  return script.sort((a, b) => (a.start ?? 0) - (b.start ?? 0));
}

export function soundingAtTime(notes: NoteEvent[], t: number): NoteEvent[] {
  return soundingAt(notes, t);
}

export function eventIndexAt(events: HarmonyEvent[], t: number): number {
  return eventAtTime(events, t);
}

export interface DerivedChorale {
  piece: PieceData;
  cadences: Cadence[];
  matches: Array<{ path: string; start: number; end: number; key: Key }>;
}

export function deriveChorale(c: ChoraleJson): DerivedChorale {
  const notes: NoteEvent[] = c.notes.map((n) => ({ ...n }));
  const measures = c.measures;
  const valid = validHarmony(c.harmony, measures);
  const homeKeyToken = valid.length ? valid[valid.length - 1].key : 'C';
  const home = parseKey(homeKeyToken);
  const core = { homeKey: homeKeyToken, measures, notes, harmony: valid, pedals: [] };
  const events = buildHarmonyEvents(core);
  const end = measures[measures.length - 1].start + measures[measures.length - 1].length;

  const regions = regionsOf(events, measures, home);

  // Cadences at the fermatas of the melody (and at the final chord).
  let fermataTimes = [...new Set(notes.filter((n) => n.line === 3 && n.fermata).map((n) => n.onset))].sort((a, b) => a - b);
  if (fermataTimes.length < 2 && c.analysisBreaks?.length) {
    // No fermatas encoded: use the analyst's line ends (double bars), on the melody note sounding there.
    const starts = new Map(measures.map((m) => [m.bar, m.start]));
    const soprano = notes.filter((n) => n.line === 3);
    fermataTimes = [
      ...new Set(
        c.analysisBreaks
          .filter(([bar]) => starts.has(bar))
          .map(([bar, beat]) => starts.get(bar)! + beat)
          .map((t) => soprano.find((n) => n.onset <= t + 1e-6 && n.onset + n.dur > t + 1e-6)?.onset)
          .filter((t): t is number => t !== undefined),
      ),
    ].sort((a, b) => a - b);
  }
  const finalOnset = Math.max(...notes.filter((n) => n.line === 3).map((n) => n.onset));
  if (!fermataTimes.includes(finalOnset)) fermataTimes.push(finalOnset);
  const cadences: Cadence[] = fermataTimes.map((at) => {
    const i = eventAtTime(events, at);
    const ev = events[i];
    const soprano = soundingAt(notes, at).find((n) => n.line === 3);
    const kind = classifyCadence(ev, events[i - 1], soprano);
    const dur = soprano ? soprano.onset + soprano.dur - at : 1;
    return { at, bar: barAt(measures, at), dur, kind, key: ev.key, event: ev, prev: events[i - 1] };
  });

  const punctuation: PunctuationSpec[] = cadences.map((cd) => {
    const inHome = sameKey(cd.key, home);
    const where = inHome ? '' : ` in ${keyName(cd.key)}`;
    const plain: Record<CadenceKind, string> = {
      pac: inHome ? 'home' : 'a new home',
      iac: inHome ? 'home, softly' : 'arrival',
      plagal: 'amen',
      tonic: 'a quiet close',
      deceptive: 'the ground gives way',
      half: 'a question',
      phrygian: 'a question',
      pause: 'a pause',
    };
    return { bar: cd.bar, at: cd.at, mark: CADENCE_MARK[cd.kind], label: plain[cd.kind], theory: `${CADENCE_NAMES[cd.kind]}${where}.` };
  });

  // Recognized paths inside the chorale.
  const matches = pathMatches(events);
  const script = scriptOf(matches, measures);

  // Phrases: from the note after one fermata to the next fermata.
  const moments: Moment[] = [];
  let phraseStart = measures[0].start;
  cadences.forEach((cd, idx) => {
    const phraseEnd = Math.min(end, cd.at + cd.dur);
    const text = sungText(notes, phraseStart, cd.at);
    const inHome = sameKey(cd.key, home);
    const keyText = keyName(cd.key);
    const soprano = soundingAt(notes, cd.at).find((n) => n.line === 3);
    const feeling: Record<CadenceKind, string> = {
      pac: inHome ? 'Arriving home' : 'Arriving in a new home',
      iac: inHome ? 'Home, but not quite final' : 'A new home, softly',
      plagal: 'A soft “Amen”',
      tonic: 'A quiet close',
      deceptive: 'The ground gives way',
      half: 'A question left open',
      phrygian: 'A question, sinking',
      pause: 'A breath',
    };
    const listen: Record<CadenceKind, string> = {
      pac: `The bass falls from the dominant to ${pcName(cd.key.tonic)} and the melody lands on the keynote${inHome ? '' : `, but in ${keyText}, not at home`}.`,
      iac: `The harmony arrives on ${pcName(cd.key.tonic)}, but the melody stops on ${soprano ? pcName(spellMidi(soprano.midi, cd.event.chord.members)) : 'another note'}, so it sounds less final.`,
      plagal: 'The subdominant falls to the tonic without a dominant: a gentle close.',
      tonic: 'The phrase settles on the tonic, but the bass does not make the full dominant-to-tonic leap.',
      deceptive: 'Everything points home, but the bass steps up and the chord turns minor.',
      half: `The phrase stops on the dominant of ${keyText} and waits for an answer.`,
      phrygian: `The bass sinks a half step onto the dominant of ${keyText}: an old, sighing way to pause.`,
      pause: 'The phrase stops on a chord that is neither home nor dominant.',
    };
    const whatIfs: WhatIf[] = [];
    let hold: HoldSpec | undefined;
    const prevEv = cd.prev;
    if ((cd.kind === 'pac' || cd.kind === 'iac') && soprano && prevEv) {
      const before = soundingAt(notes, cd.at - 0.01).filter((n) => n.line !== undefined);
      const lines = new Map<number, NoteEvent>();
      before.forEach((n) => lines.set(n.line!, n));
      const beforeList = [...lines.values()];
      const vi = cd.key.mode === 'major' ? 'vi' : 'VI';
      const alt = reharmonize(vi, cd.key, beforeList, prevEv.chord, soprano.midi);
      const barBeat = cd.at - barStart(measures, cd.bar);
      if (alt) {
        whatIfs.push({
          id: `deceive-${idx + 1}`,
          label: 'Let the ground give way',
          question: 'What if this cadence were deceptive?',
          effect: `The melody stays the same, but the bass steps up and the chord becomes ${chordName(alt.chord)} instead of ${chordName(cd.event.chord)}. Chord voiced by the engine.`,
          ops: [{ kind: 'replaceSpan', bar: cd.bar, start: cd.at, end: cd.at + cd.dur, notes: alt.names.map((name, i) => ({ name, line: i })) }],
          harmony: [{ bar: cd.bar, beat: barBeat, key: cd.event.spec.key, rn: vi }],
          punctuation: [{ bar: cd.bar, at: cd.at, mark: 'ellipsis', label: 'the ground gives way', theory: 'Deceptive cadence (engine realization).' }],
        });
      }
      const original = soundingAt(notes, cd.at).filter((n) => n.onset >= cd.at - 1e-6).sort((a, b) => a.midi - b.midi);
      const options: HoldOption[] = [
        {
          id: 'bach',
          label: chordName(cd.event.chord),
          hint: inHome ? 'home' : 'a new home',
          chord: { notes: original.map((n) => n.name), dur: cd.dur },
          key: cd.event.spec.key,
          rn: cd.event.spec.rn,
          composer: true,
        },
      ];
      if (alt) options.push({ id: 'vi', label: chordName(alt.chord), hint: 'the ground gives way', chord: { notes: alt.names, dur: cd.dur }, key: cd.event.spec.key, rn: vi });
      const iv = reharmonize(cd.key.mode === 'major' ? 'IV' : 'iv', cd.key, beforeList, prevEv.chord, soprano.midi);
      if (iv) options.push({ id: 'iv', label: chordName(iv.chord), hint: 'somewhere softer', chord: { notes: iv.names, dur: cd.dur }, key: cd.event.spec.key, rn: cd.key.mode === 'major' ? 'IV' : 'iv' });
      if (options.length > 1) {
        hold = { at: cd.at, resolveLength: cd.dur, prompt: 'The dominant is sounding under the last note of the line. Where should it land?', options };
      }
    }
    const pathIds = new Set<string>();
    const cadPath = CADENCE_PATH[cd.kind];
    if (cadPath) pathIds.add(cadPath);
    script.filter((s) => (s.start ?? 0) >= phraseStart - 1e-6 && (s.end ?? 0) <= phraseEnd + 1e-6).forEach((s) => pathIds.add(s.path));
    const feelingIds: Record<CadenceKind, string[]> = {
      pac: ['home'],
      iac: ['home'],
      plagal: ['settling'],
      tonic: ['settling'],
      deceptive: ['give-way'],
      half: ['comma'],
      phrygian: ['comma', 'sighing'],
      pause: [],
    };
    moments.push({
      id: `phrase-${idx + 1}`,
      from: barAt(measures, phraseStart),
      to: cd.bar,
      start: phraseStart,
      end: phraseEnd,
      text,
      feeling: feeling[cd.kind],
      listenFor: listen[cd.kind],
      theoryTitle: `Phrase ${idx + 1}: ${CADENCE_NAMES[cd.kind].toLowerCase()}${inHome ? '' : ` in ${keyText}`}`,
      theory: `The phrase ends ${prevEv ? `${prevEv.spec.rn} – ` : ''}${cd.event.spec.rn} in ${keyText}. Cadence type derived by rule from the harmonic analysis at the fermata.`,
      paths: [...pathIds],
      feelings: feelingIds[cd.kind],
      whatIfs,
      hold,
    });
    phraseStart = phraseEnd;
  });

  const bwvLabel = `BWV ${c.bwv}`;
  const firstLine = FIRST_LINES[c.id];
  const stanza = c.stanza ? `stanza ${c.stanza.replace(',', '–')}` : '';
  const ofHymn = firstLine && firstLine !== c.title ? `${stanza || 'a stanza'} of “${c.title}”` : stanza;
  const piece: PieceData = {
    id: c.id,
    kind: 'chorale',
    title: firstLine ?? c.title,
    catalogue: bwvLabel,
    collection: c.work ? `Chorale from the ${c.work}` : 'Four-part chorale',
    composer: 'Johann Sebastian Bach',
    subtitle: c.derivedFrom
      ? [ofHymn, `same harmony as BWV ${c.derivedFrom.replace('bwv', '').replace('-', '.')}, a semitone lower (derived by transposition)`].filter(Boolean).join(' · ')
      : [ofHymn, `Riemenschneider no. ${c.riemenschneider}`].filter(Boolean).join(' · '),
    tune: c.tune,
    riemenschneider: c.riemenschneider,
    homeKey: homeKeyToken,
    tempo: 60,
    measures,
    notes,
    harmony: valid,
    pedals: [],
    regions,
    punctuation,
    script,
    moments,
    sources: [
      { label: 'Score: music21 corpus (Bach chorales)', url: 'https://github.com/cuthbertLab/music21', licence: 'see PROVENANCE.md' },
      { label: `Harmonic analysis: ${c.analyst || 'When in Rome'}${c.proofreader ? `, proofread by ${c.proofreader}` : ''} (When in Rome)`, url: 'https://github.com/MarkGotham/When-in-Rome', licence: 'CC BY-SA 4.0' },
    ],
    editorial:
      'Key regions, cadences, phrases, paths, what-ifs and holds on this page are derived automatically by rule from the notes and the published analysis.' +
      (c.words && !c.words.kept && c.words.reason !== 'no words encoded' ? ` Words are not shown: the ${c.words.reason.replace(/^encoded words/, 'words encoded in the score')}.` : ''),
  };
  return { piece, cadences, matches };
}
